import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { HeatCell, Pill, SeverityBadge, label } from '@/features/dossier/components/primitives'
import { listScandals, type ScandalRow } from './api'

/**
 * The integrity roll-up, across every target.
 *
 * ## Sorted by evidence, not by severity
 *
 * A severity-90 surface with nothing behind it is a suspicion; a severity-60 one with two
 * independent primary records is a briefing. Ordering by evidence strength puts the second
 * first, which is the opposite of what a campaign's instinct does — and is why the column is
 * on the row rather than in a drawer.
 */
export function ScandalsPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<ScandalRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setRows(await listScandals())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (error && !rows) return <ErrorStrip message={error} />
  if (!rows) return <LoadingBlock label="Loading" />

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Scandals & Legal"
        description="Ordered by how well evidenced each exposure is — not by how damaging it
                     looks."
      />
      <Panel>
        <ErpTable head={<>
          <Th>Target</Th>
          <Th>Exposure</Th>
          <Th className="axis-hide-sm">Category</Th>
          <Th className="text-right">Severity</Th>
          <Th className="text-right">Evidence</Th>
          <Th>Status</Th>
        </>}>
          {rows.length === 0 && (
            <EmptyRow colSpan={6}
                      message="No documented exposures yet. Record one on a target's page." />
          )}
          {rows.map((row) => (
            <tr key={row.surfaceId} className="cursor-pointer hover:bg-muted/40"
                onClick={() => navigate(`/opposition/targets/${row.targetId}`)}>
              <Td><span className="font-medium">{row.targetName}</span></Td>
              <Td>{row.surface}</Td>
              <Td className="axis-hide-sm">
                <Pill className="border-border">{label(row.categoryCode)}</Pill>
              </Td>
              <Td className="text-right"><HeatCell value={row.severity} /></Td>
              <Td className="text-right"><HeatCell value={row.evidenceStrength} /></Td>
              <Td>
                {row.statusCode === 'STALE'
                  ? <SeverityBadge severity="HIGH" />
                  : <Pill className="border-border">{label(row.statusCode)}</Pill>}
                {row.statusCode === 'STALE' && (
                  <span className="ml-1 text-[10px] text-muted-foreground">
                    evidence disputed
                  </span>
                )}
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>
    </div>
  )
}
